import { useState } from 'react'
import { Modal } from 'antd'
import styled, { css } from 'styled-components'
import { IMAGE_STYLE_OPTIONS, type StyleOption } from './styleOptions'
import { TYPOGRAPHY, FONT_WEIGHT } from '../../styles/theme'

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
`

interface CardProps {
  $selected: boolean
}

const StyleCard = styled.button<CardProps>`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  padding: 0;
  border-radius: 10px;
  cursor: pointer;
  text-align: left;
  overflow: hidden;
  transition:
    border-color 0.15s ease,
    background 0.15s ease;
  background: var(--color-bg-card);
  border: 2px solid
    ${({ $selected }) => ($selected ? 'var(--color-primary)' : 'var(--color-border-card)')};

  ${({ $selected }) =>
    $selected &&
    css`
      background: var(--color-primary-bg);
    `}

  &:hover:not(:disabled) {
    border-color: var(--color-primary);
  }
`

const PreviewImage = styled.img`
  width: 100%;
  aspect-ratio: 16 / 9;
  object-fit: cover;
  display: block;
  background: var(--color-bg-hover);
`

const PreviewPlaceholder = styled.div`
  width: 100%;
  aspect-ratio: 16 / 9;
  background: var(--color-bg-hover);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${TYPOGRAPHY.caption.fontSize};
  color: var(--color-text-tertiary, var(--color-text-secondary));
  letter-spacing: 0.02em;
`

const CardBody = styled.div`
  padding: 10px 12px 12px;
  display: flex;
  flex-direction: column;
  gap: 3px;
`

const NameRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
`

const StyleName = styled.span<CardProps>`
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-weight: ${FONT_WEIGHT.semibold};
  color: ${({ $selected }) => ($selected ? 'var(--color-primary)' : 'var(--color-text-primary)')};
`

const DefaultTag = styled.span`
  margin-left: auto;
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-weight: ${FONT_WEIGHT.semibold};
  color: var(--color-primary);
  background: var(--color-primary-bg);
  border: 1px solid var(--color-primary);
  border-radius: 4px;
  padding: 2px 8px;
  line-height: 1.5;
  white-space: nowrap;
  flex-shrink: 0;
`

const SetDefaultButton = styled.button`
  margin-left: auto;
  padding: 0;
  border: none;
  background: none;
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-weight: ${FONT_WEIGHT.medium};
  color: var(--color-text-secondary);
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
  opacity: 0.6;
  transition:
    opacity 0.12s ease,
    color 0.12s ease;

  &:hover {
    opacity: 1;
    color: var(--color-primary);
  }
`

const StyleDesc = styled.span`
  font-size: ${TYPOGRAPHY.caption.fontSize};
  color: var(--color-text-secondary);
  line-height: 1.4;
`

interface StyleSelectorModalProps {
  open: boolean
  onClose: () => void
  value: string
  onChange: (id: string) => void
  defaultStyleId?: string
  onSetAsDefault?: (id: string) => void
}

export function StyleSelectorModal({
  open,
  onClose,
  value,
  onChange,
  defaultStyleId,
  onSetAsDefault,
}: StyleSelectorModalProps) {
  const [hoveredId, setHoveredId] = useState<string | null>(null)

  const handleSelect = (style: StyleOption) => {
    onChange(style.id)
    onClose()
  }

  return (
    <Modal
      title="Image Style"
      open={open}
      onCancel={onClose}
      footer={null}
      width="62vw"
      style={{ maxWidth: 1100, minWidth: 640 }}
      centered
    >
      <Grid>
        {IMAGE_STYLE_OPTIONS.map((style) => {
          const selected = value === style.id
          const isDefault = style.id === defaultStyleId
          const showSetDefault = onSetAsDefault && !isDefault && hoveredId === style.id

          return (
            <StyleCard
              key={style.id}
              $selected={selected}
              onClick={() => handleSelect(style)}
              onMouseEnter={() => setHoveredId(style.id)}
              onMouseLeave={() => setHoveredId(null)}
              type="button"
              aria-pressed={selected}
            >
              {style.previewImageUrl ? (
                <PreviewImage src={style.previewImageUrl} alt={style.name} />
              ) : (
                <PreviewPlaceholder>Preview</PreviewPlaceholder>
              )}
              <CardBody>
                <NameRow>
                  <StyleName $selected={selected}>{style.name}</StyleName>
                  {isDefault && <DefaultTag>Default</DefaultTag>}
                  {showSetDefault && (
                    <SetDefaultButton
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation()
                        onSetAsDefault(style.id)
                      }}
                    >
                      Set as default
                    </SetDefaultButton>
                  )}
                </NameRow>
                <StyleDesc>{style.description}</StyleDesc>
              </CardBody>
            </StyleCard>
          )
        })}
      </Grid>
    </Modal>
  )
}
